/**
 * batch-67 portal-thema (FO-504, spec 204) - feestelijke thema's op het
 * portal-dashboard: de zes thema-soorten, hun labels en prioriteit, het
 * gedeelde instellingen-schema (API valideert ermee, het beheerformulier
 * valideert er client-side mee) met expliciete defaults, en het DTO-veld
 * `thema` dat op de dashboard-identiteitsfetch (`GET /leave/v2/me`) meereist.
 *
 * De rekenregels zelf (welk thema geldt op welke Europe/Amsterdam-dag) staan
 * puur in `@streep/utils` (`bepaalPortalThema`); dit bestand is uitsluitend de
 * vorm. Server-veilig: geen React, geen 'use client' (de backoffice-pagina
 * importeert de constanten in een server component).
 */

import { z } from 'zod'

// ── Soorten ───────────────────────────────────────────────────────────────────

export const PORTAL_THEMA_SOORTEN = [
  'VERJAARDAG',
  'SINTERKLAAS',
  'KERST',
  'NIEUWJAAR',
  'PASEN',
  'KONINGSDAG',
] as const
export type PortalThemaSoort = (typeof PORTAL_THEMA_SOORTEN)[number]

export const PORTAL_THEMA_LABELS: Record<PortalThemaSoort, string> = {
  VERJAARDAG: 'Verjaardag',
  SINTERKLAAS: 'Sinterklaas',
  KERST: 'Kerst',
  NIEUWJAAR: 'Nieuwjaar',
  PASEN: 'Pasen',
  KONINGSDAG: 'Koningsdag',
}

/**
 * Prioriteit bij overlap (een dag heeft nooit twee thema's): de verjaardag
 * wint altijd van een feestdag; daarna gaan de losse dagen (Koningsdag, Pasen)
 * vóór de lange decemberperiodes, zodat een verschoven periode in het beheer
 * een berekende feestdag nooit kan verdringen.
 */
export const PORTAL_THEMA_PRIORITEIT: readonly PortalThemaSoort[] = [
  'VERJAARDAG',
  'KONINGSDAG',
  'PASEN',
  'SINTERKLAAS',
  'KERST',
  'NIEUWJAAR',
]

/**
 * Thema's met een vaste jaarlijkse periode (van/tot als dag-in-het-jaar), in
 * prioriteitsvolgorde (zelfde onderlinge volgorde als `PORTAL_THEMA_PRIORITEIT`).
 */
export const PORTAL_THEMA_VASTE_PERIODE = ['SINTERKLAAS', 'KERST', 'NIEUWJAAR'] as const
export type PortalThemaVastePeriodeSoort = (typeof PORTAL_THEMA_VASTE_PERIODE)[number]

/** Thema's rond een berekende dag (Pasen, Koningsdag): dagen ervoor en erna. */
export const PORTAL_THEMA_BEREKEND = ['PASEN', 'KONINGSDAG'] as const
export type PortalThemaBerekendSoort = (typeof PORTAL_THEMA_BEREKEND)[number]

// ── Instellingen (gedeeld schema) ─────────────────────────────────────────────

/**
 * Maximale lengte van de eigen korte tekst per thema. De banner toont de tekst
 * in hooguit twee regels. Hoeveel tekens daarin passen hangt af van de letters
 * en de breedte van het toestel: gemeten past een zin van 60 tekens op 375 px
 * en net niet op 360 px. Het maximum is dus een bovengrens, geen garantie; het
 * voorbeeld in het beheer (360 px breed) laat zien of een tekst past.
 */
export const PORTAL_THEMA_TEKST_MAX = 60

/** Maximaal aantal dagen vóór/na een berekende feestdag. */
export const PORTAL_THEMA_DAGEN_MAX = 14

const DAGEN_PER_MAAND = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const

/**
 * Dag in het jaar (maand 1-12, dag 1-31). Februari gaat tot 28: een grens op
 * 29 februari bestaat in drie van de vier jaren niet en zou het thema dan stil
 * laten wegvallen.
 */
export const dagInJaarSchema = z
  .object({
    maand: z.number().int().min(1, 'Kies een maand').max(12, 'Kies een maand'),
    dag: z.number().int().min(1, 'Kies een dag').max(31, 'Kies een dag'),
  })
  .superRefine((waarde, ctx) => {
    const max = DAGEN_PER_MAAND[waarde.maand - 1]
    if (max !== undefined && waarde.dag > max) {
      ctx.addIssue({
        code: 'custom',
        path: ['dag'],
        message: `Deze maand heeft maximaal ${max} dagen`,
      })
    }
  })
export type DagInJaar = z.infer<typeof dagInJaarSchema>

const themaTekstSchema = z
  .string()
  .trim()
  .max(PORTAL_THEMA_TEKST_MAX, `Maximaal ${PORTAL_THEMA_TEKST_MAX} tekens`)
  .nullable()

const dagenSchema = z
  .number()
  .int('Vul een geheel aantal dagen in')
  .min(0, 'Minimaal 0 dagen')
  .max(PORTAL_THEMA_DAGEN_MAX, `Maximaal ${PORTAL_THEMA_DAGEN_MAX} dagen`)

export const portalThemaVerjaardagSchema = z.object({
  actief: z.boolean(),
  /** Eigen korte tekst (de regel onder de kop); `null` = de standaardtekst. */
  tekst: themaTekstSchema,
  /** Leeftijd tonen in de felicitatie. Default UIT (spec A2). */
  leeftijdTonen: z.boolean(),
})
export type PortalThemaVerjaardagInstelling = z.infer<typeof portalThemaVerjaardagSchema>

export const portalThemaVastSchema = z.object({
  actief: z.boolean(),
  tekst: themaTekstSchema,
  /** Eerste dag van de periode (inclusief). */
  van: dagInJaarSchema,
  /** Laatste dag van de periode (inclusief). Mag vóór `van` liggen: dan loopt de periode over de jaarwisseling. */
  tot: dagInJaarSchema,
})
export type PortalThemaVastInstelling = z.infer<typeof portalThemaVastSchema>

export const portalThemaBerekendSchema = z.object({
  actief: z.boolean(),
  tekst: themaTekstSchema,
  /** Dagen vóór de berekende dag waarop het thema al geldt (Pasen: 2 = Goede Vrijdag). */
  dagenVoor: dagenSchema,
  /** Dagen ná de berekende dag waarop het thema nog geldt (Pasen: 1 = Paasmaandag). */
  dagenNa: dagenSchema,
})
export type PortalThemaBerekendInstelling = z.infer<typeof portalThemaBerekendSchema>

/** Dag-in-het-jaar als vergelijkbaar getal (maand*100 + dag). */
export function dagInJaarGetal(d: DagInJaar): number {
  return d.maand * 100 + d.dag
}

/**
 * Valt `md` (maand*100+dag) in de inclusieve periode van..tot? Een periode
 * waarvan `tot` vóór `van` ligt loopt over de jaarwisseling (27-12 t/m 01-01).
 */
export function valtInVastePeriode(md: number, van: DagInJaar, tot: DagInJaar): boolean {
  const v = dagInJaarGetal(van)
  const t = dagInJaarGetal(tot)
  if (v <= t) return md >= v && md <= t
  return md >= v || md <= t
}

/** Alle (maand*100+dag)-waarden die een vaste periode beslaat (voor de overlap-controle). */
function dagenVanPeriode(van: DagInJaar, tot: DagInJaar): Set<number> {
  const uit = new Set<number>()
  for (let maand = 1; maand <= 12; maand++) {
    const max = DAGEN_PER_MAAND[maand - 1] ?? 31
    for (let dag = 1; dag <= max; dag++) {
      const md = maand * 100 + dag
      if (valtInVastePeriode(md, van, tot)) uit.add(md)
    }
  }
  return uit
}

/** Melding van de overlap-controle (herkenbaar voor de tolerante normalisatie). */
const OVERLAP_MELDING = 'De periode overlapt met'
/** Melding van de december/januari-regel voor Nieuwjaar. */
const NIEUWJAAR_PERIODE_MELDING = 'Nieuwjaar mag alleen dagen in december en januari bevatten'

/**
 * Bevat de periode een dag buiten december en januari? De kop van Nieuwjaar
 * telt in december af en wenst daarna "Gelukkig nieuwjaar"; elke dag in een
 * andere maand zou die wens midden in het jaar tonen. Getoetst op de dagen die
 * de periode beslaat, niet op de startmaand: 27-12 t/m 20-11 loopt over de
 * jaarwisseling en beslaat dus bijna het hele jaar, terwijl 01-01 t/m 03-01
 * gewoon geldig is.
 */
function nieuwjaarBuitenDecemberJanuari(van: DagInJaar, tot: DagInJaar): boolean {
  for (const md of dagenVanPeriode(van, tot)) {
    const maand = Math.floor(md / 100)
    if (maand !== 12 && maand !== 1) return true
  }
  return false
}

export const portalThemaInstellingenSchema = z
  .object({
    VERJAARDAG: portalThemaVerjaardagSchema,
    SINTERKLAAS: portalThemaVastSchema,
    KERST: portalThemaVastSchema,
    NIEUWJAAR: portalThemaVastSchema,
    PASEN: portalThemaBerekendSchema,
    KONINGSDAG: portalThemaBerekendSchema,
  })
  .superRefine((inst, ctx) => {
    // Nieuwjaar: alleen dagen in december en januari (op beide velden, want
    // een tikfout in "van" of in "tot" kan de oorzaak zijn).
    if (nieuwjaarBuitenDecemberJanuari(inst.NIEUWJAAR.van, inst.NIEUWJAAR.tot)) {
      for (const veld of ['van', 'tot'] as const) {
        ctx.addIssue({ code: 'custom', path: ['NIEUWJAAR', veld], message: NIEUWJAAR_PERIODE_MELDING })
      }
    }
    // Twee actieve vaste periodes mogen elkaar niet overlappen: de prioriteit
    // zou dan stil een thema wegdrukken. Liever een veldfout in het beheer, op
    // BEIDE periodes zodat de fout verdwijnt welke van de twee je ook aanpast.
    const actief = PORTAL_THEMA_VASTE_PERIODE.filter((s) => inst[s].actief)
    for (let i = 0; i < actief.length; i++) {
      for (let j = i + 1; j < actief.length; j++) {
        const a = actief[i]
        const b = actief[j]
        const dagenA = dagenVanPeriode(inst[a].van, inst[a].tot)
        const dagenB = dagenVanPeriode(inst[b].van, inst[b].tot)
        const overlapt = [...dagenA].some((md) => dagenB.has(md))
        if (overlapt) {
          ctx.addIssue({
            code: 'custom',
            path: [a, 'tot'],
            message: `${OVERLAP_MELDING} ${PORTAL_THEMA_LABELS[b]}`,
          })
          ctx.addIssue({
            code: 'custom',
            path: [b, 'van'],
            message: `${OVERLAP_MELDING} ${PORTAL_THEMA_LABELS[a]}`,
          })
        }
      }
    }
  })
export type PortalThemaInstellingen = z.infer<typeof portalThemaInstellingenSchema>

/**
 * Expliciete defaults (spec 204 §2.A, aanname A1/A2). Geen verborgen defaults:
 * de API leest de opgeslagen rij hier overheen, het beheer toont deze waarden
 * zolang er niets is opgeslagen.
 */
export const PORTAL_THEMA_DEFAULTS: PortalThemaInstellingen = {
  VERJAARDAG: { actief: true, tekst: null, leeftijdTonen: false },
  SINTERKLAAS: { actief: true, tekst: null, van: { maand: 12, dag: 1 }, tot: { maand: 12, dag: 5 } },
  KERST: { actief: true, tekst: null, van: { maand: 12, dag: 6 }, tot: { maand: 12, dag: 26 } },
  NIEUWJAAR: { actief: true, tekst: null, van: { maand: 12, dag: 27 }, tot: { maand: 1, dag: 1 } },
  PASEN: { actief: true, tekst: null, dagenVoor: 2, dagenNa: 1 },
  KONINGSDAG: { actief: true, tekst: null, dagenVoor: 0, dagenNa: 0 },
}

export interface PortalThemaNormalisatie {
  instellingen: PortalThemaInstellingen
  /** Wat er aan de opgeslagen waarde gerepareerd is (leeg = niets). Voor een WARN in de API. */
  meldingen: string[]
}

/**
 * Tolerant lezen van een opgeslagen waarde, PER VELD: elk veld dat klopt
 * blijft staan, alleen een ongeldig veld krijgt zijn default. Eén verouderd
 * veld (bijvoorbeeld een tekst die na een aanscherping te lang is) zet zo nooit
 * een door HR uitgezet thema stil weer aan en gooit ook de periode of de eigen
 * tekst van dat thema niet weg. Een te lange tekst wordt ingekort.
 *
 * Klopt de samenstelling daarna als geheel niet (overlap, Nieuwjaar buiten
 * december en januari), dan wordt zo min mogelijk gerepareerd: bij overlap gaat
 * alleen het thema met de lagere prioriteit uit, een ongeldige Nieuwjaar-
 * periode valt terug op de standaardperiode. Een half-oude of corrupte rij kan
 * dus nooit het hele dashboard-thema laten wegvallen.
 */
export function normaliseerPortalThemaInstellingenMetMeldingen(
  waarde: unknown,
): PortalThemaNormalisatie {
  const meldingen: string[] = []
  const isObject = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v)
  const bron = isObject(waarde) ? waarde : {}

  const themaBron = (soort: PortalThemaSoort): Record<string, unknown> => {
    const v = bron[soort]
    if (isObject(v)) return v
    if (v !== undefined) meldingen.push(`${soort}: opgeslagen waarde ongeldig, standaard gebruikt`)
    return {}
  }
  const veld = <T>(schema: z.ZodType<T>, v: unknown, fallback: T, pad: string): T => {
    if (v === undefined) return fallback
    const r = schema.safeParse(v)
    if (r.success) return r.data
    meldingen.push(`${pad}: ongeldig, standaard gebruikt`)
    return fallback
  }
  const tekst = (v: unknown, pad: string): string | null => {
    if (v === undefined || v === null) return null
    if (typeof v !== 'string') {
      meldingen.push(`${pad}: ongeldig, standaard gebruikt`)
      return null
    }
    const t = v.trim()
    if (t.length <= PORTAL_THEMA_TEKST_MAX) return t
    meldingen.push(`${pad}: langer dan ${PORTAL_THEMA_TEKST_MAX} tekens, ingekort`)
    // Per teken (code point) inkorten, niet per UTF-16-eenheid: een emoji die
    // doormidden wordt geknipt laat een losse surrogaat achter, en die weigert
    // de jsonb-kolom bij het eerstvolgende opslaan.
    let kort = ''
    for (const teken of t) {
      if (kort.length + teken.length > PORTAL_THEMA_TEKST_MAX) break
      kort += teken
    }
    return kort.trimEnd()
  }
  const vast = (soort: PortalThemaVastePeriodeSoort): PortalThemaVastInstelling => {
    const b = themaBron(soort)
    const d = PORTAL_THEMA_DEFAULTS[soort]
    return {
      actief: veld(z.boolean(), b.actief, d.actief, `${soort}.actief`),
      tekst: tekst(b.tekst, `${soort}.tekst`),
      van: veld(dagInJaarSchema, b.van, d.van, `${soort}.van`),
      tot: veld(dagInJaarSchema, b.tot, d.tot, `${soort}.tot`),
    }
  }
  const berekend = (soort: PortalThemaBerekendSoort): PortalThemaBerekendInstelling => {
    const b = themaBron(soort)
    const d = PORTAL_THEMA_DEFAULTS[soort]
    return {
      actief: veld(z.boolean(), b.actief, d.actief, `${soort}.actief`),
      tekst: tekst(b.tekst, `${soort}.tekst`),
      dagenVoor: veld(dagenSchema, b.dagenVoor, d.dagenVoor, `${soort}.dagenVoor`),
      dagenNa: veld(dagenSchema, b.dagenNa, d.dagenNa, `${soort}.dagenNa`),
    }
  }
  const verjaardagBron = themaBron('VERJAARDAG')
  const uit: PortalThemaInstellingen = {
    VERJAARDAG: {
      actief: veld(
        z.boolean(),
        verjaardagBron.actief,
        PORTAL_THEMA_DEFAULTS.VERJAARDAG.actief,
        'VERJAARDAG.actief',
      ),
      tekst: tekst(verjaardagBron.tekst, 'VERJAARDAG.tekst'),
      leeftijdTonen: veld(
        z.boolean(),
        verjaardagBron.leeftijdTonen,
        PORTAL_THEMA_DEFAULTS.VERJAARDAG.leeftijdTonen,
        'VERJAARDAG.leeftijdTonen',
      ),
    },
    SINTERKLAAS: vast('SINTERKLAAS'),
    KERST: vast('KERST'),
    NIEUWJAAR: vast('NIEUWJAAR'),
    PASEN: berekend('PASEN'),
    KONINGSDAG: berekend('KONINGSDAG'),
  }

  // De samenstelling moet ook als geheel kloppen; repareer zo min mogelijk.
  // Hooguit één Nieuwjaar-reparatie plus twee thema's uitzetten, dus vier
  // rondes (de laatste is de geslaagde controle).
  for (let ronde = 0; ronde < PORTAL_THEMA_VASTE_PERIODE.length + 1; ronde++) {
    const geheel = portalThemaInstellingenSchema.safeParse(uit)
    if (geheel.success) return { instellingen: uit, meldingen }
    let gerepareerd = false
    for (const issue of geheel.error.issues) {
      const soort = issue.path[0]
      const veldNaam = issue.path[1]
      if (soort === 'NIEUWJAAR' && issue.message === NIEUWJAAR_PERIODE_MELDING) {
        uit.NIEUWJAAR = {
          ...uit.NIEUWJAAR,
          van: PORTAL_THEMA_DEFAULTS.NIEUWJAAR.van,
          tot: PORTAL_THEMA_DEFAULTS.NIEUWJAAR.tot,
        }
        meldingen.push(
          'NIEUWJAAR: periode bevatte dagen buiten december en januari, standaardperiode gebruikt',
        )
        gerepareerd = true
        break
      }
      // Overlap: alleen het thema met de LAGERE prioriteit (de `van`-kant) gaat uit.
      if (
        veldNaam === 'van' &&
        issue.message.startsWith(OVERLAP_MELDING) &&
        (PORTAL_THEMA_VASTE_PERIODE as readonly string[]).includes(String(soort))
      ) {
        const s = soort as PortalThemaVastePeriodeSoort
        uit[s] = { ...uit[s], actief: false }
        meldingen.push(`${s}: periode overlapt met een thema met hogere prioriteit, uitgezet`)
        gerepareerd = true
        break
      }
    }
    if (!gerepareerd) break
  }
  // Vangnet; met de reparaties hierboven niet bereikbaar.
  meldingen.push('opgeslagen samenstelling onherstelbaar ongeldig, defaults gebruikt')
  return { instellingen: { ...PORTAL_THEMA_DEFAULTS }, meldingen }
}

/** Zie `normaliseerPortalThemaInstellingenMetMeldingen`; alleen de instellingen. */
export function normaliseerPortalThemaInstellingen(waarde: unknown): PortalThemaInstellingen {
  return normaliseerPortalThemaInstellingenMetMeldingen(waarde).instellingen
}

// ── DTO op het dashboard ──────────────────────────────────────────────────────

/**
 * Het thema van vandaag voor de ingelogde medewerker, server-side bepaald in
 * Europe/Amsterdam en meegestuurd op `GET /leave/v2/me` (de identiteitsfetch
 * van het portal-dashboard én de portal-layout). `null` = geen thema.
 *
 * Spec 204 §2.A.1 noemt een `periode`-veld in de returnwaarde; dat is bewust
 * weggelaten: de portal heeft alleen de dag zelf nodig (wegklik-sleutel en de
 * "is dit nog vandaag"-controle), de periode leeft in de instellingen.
 */
export interface PortalThemaDTO {
  soort: PortalThemaSoort
  /** De Europe/Amsterdam-kalenderdag ('yyyy-mm-dd') waarop dit thema geldt; sleutel voor het wegklikken. */
  dag: string
  /** Kop van de banner, bv. "Gefeliciteerd, Jelle!" of "Fijne pakjesavond". */
  titel: string
  /** Regel onder de kop (eigen tekst uit het beheer of de standaardtekst). */
  tekst: string | null
  /** Alleen bij VERJAARDAG én leeftijd-tonen aan; anders null. */
  leeftijd: number | null
  /** Hoogtepunt binnen de periode (5-12, 24 t/m 26-12, 31-12 en 1-1, Eerste en Tweede Paasdag, Koningsdag, de verjaardag zelf): voller motief. */
  hoogtepunt: boolean
}
